import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { applyFacetMerge, toggleMergeFacetSelection } from 'store/slices/RoofEditSlice';
import { syncDeletedRoofIndexes } from 'store/slices/ToolSlice';
import { appToast } from 'components/Basic/CustomToast';
import { mergeSelectedFacets } from './mergeFacets';

/**
 * Merge mode click handling: the first click only selects a facet, a click on an
 * adjacent facet merges the two right away (smaller into larger, ties keep the
 * earlier one). The merge result is left unselected, so clicking it starts a new
 * merge selection rather than chaining onto the previous one.
 */
export default function useMergeFacets() {
	const { mergeSelectedFacetIds, editableFacets } = useSelector((state: RootState) => state.roofEditData.data);
	const dispatch = useDispatch();

	return useCallback((segmentId: string) => {
		// nothing selected yet, or the same facet clicked again -> just toggle selection
		if (!mergeSelectedFacetIds.length || mergeSelectedFacetIds.includes(segmentId)) {
			dispatch(toggleMergeFacetSelection({ segmentId }));
			return;
		}

		const mergeResult = mergeSelectedFacets([...mergeSelectedFacetIds, segmentId], editableFacets);

		if (!mergeResult) {
			appToast.warn('Selected facets cannot be merged.');
			return;
		}

		// console.log('[merge facets]', {
		// 	mergeResult,
		// 	mergedFacetIDs: mergeResult.removedFacetIds,
		// 	mergedToFacetID: mergeResult.mergedFacet.segmentId,
		// 	deletedFacetIDs: mergeResult.removedFacetIds,
		// });

		dispatch(applyFacetMerge(mergeResult));
	}, [dispatch, editableFacets, mergeSelectedFacetIds]);
}

/**
 * Keeps `deletedRoofIndexes` in sync with the facets removed by merging.
 * Driven by the edit-facet history state, so undo/redo of a merge brings the
 * merged-away facets back (and taking the merge forward removes them again).
 */
export function useSyncMergedFacetDeletions() {
	const { facetHistory, mergeDeletedFacetIds } = useSelector((state: RootState) => state.roofEditData.data);
	const { deletedRoofIndexes } = useSelector((state: RootState) => state.toolNewPostions.data);
	const dispatch = useDispatch();
	const deletedRoofIndexesRef = useRef(deletedRoofIndexes);
	deletedRoofIndexesRef.current = deletedRoofIndexes;

	// every facet merged away at any point of the history, undone ones included
	const allMergeDeletedIds = useMemo(() => (
		facetHistory.flatMap(entry => entry.mergeDeletedFacetIds ?? [])
	), [facetHistory]);

	useEffect(() => {
		const currentDeleted = deletedRoofIndexesRef.current;
		// facets deleted by the delete-facet tool must be kept as they are
		const baseDeleted = currentDeleted.filter(id => !allMergeDeletedIds.includes(id));
		const nextDeleted = [...baseDeleted, ...mergeDeletedFacetIds];

		const isUnchanged = nextDeleted.length === currentDeleted.length &&
			nextDeleted.every(id => currentDeleted.includes(id));
		if (isUnchanged) return;

		console.log('[merge facets] deleted roof indexes', nextDeleted);
		dispatch(syncDeletedRoofIndexes({ deletedRoofIndexes: nextDeleted }));
	}, [allMergeDeletedIds, dispatch, mergeDeletedFacetIds]);
}
